/**
 * Phase 5 — conflict resolution bus.
 *
 * The server returns 409 + { conflict: true, serverRecord } when a save
 * would overwrite changes made by someone else since the client last read
 * the record (common when two auditors sync the same site after being
 * offline). Resolving that needs a decision from the auditor, but the
 * decision has to happen deep inside a service call, far from any mounted
 * dialog component.
 *
 * This is a tiny pub/sub bridge: a service calls `requestResolution(...)`
 * and gets back a Promise that only resolves once the auditor picks an
 * option in the dialog that's mounted once near the app root and
 * subscribes with `onConflict(...)`.
 */

let listener = null

export function onConflict(handler) {
  listener = handler
  return () => {
    if (listener === handler) listener = null
  }
}

/**
 * @param {object} details - { entityLabel, serverRecord, localData }
 * @returns {Promise<"overwrite"|"discard">}
 */
export function requestResolution(details) {
  return new Promise((resolve) => {
    if (!listener) {
      // No dialog mounted (e.g. a background/non-UI context) — default to
      // the safe choice of not clobbering someone else's changes.
      console.log("[v0] Conflict raised with no resolution UI mounted, defaulting to discard")
      resolve("discard")
      return
    }
    listener({ ...details, resolve })
  })
}
